import dayjs from 'dayjs';
import React, { useCallback, useEffect, useState } from 'react';
import { matchPath, useLocation } from 'react-router-dom';

import { useAnalytics } from '../../../app/analytics';
import { Paths } from '../../../app/constants/paths';
import { windowWidthBreakpoints } from '../../../app/global-styles';
import { ChartInteractionUtil } from '../../../app/utils/chart-interaction';
import { TimeRangeUtil } from '../../../app/utils/time';
import { useLayoutContext } from '../../context/layout-context';
import { useViewContext } from '../../context/view-context';

import { StyledActions } from './styles';

import { Icon, Tooltip } from '@controlrooms/components';
import { ICONS } from '@controlrooms/constants';
import { useKeyPress } from '@controlrooms/hooks';
import { TimeRanges, ViewType, ZoomTo } from '@controlrooms/models';

export const ANALYZE_MIN_ZOOM = dayjs.duration(1, 's').as('seconds');
export const MONITOR_MIN_ZOOM = dayjs.duration(1, 'h').as('seconds');

export const TimelineActions: React.FC = () => {
  const { pathname } = useLocation();
  const { updateTimeSelection, recordTimelineHistory, viewState, handleUndoTimeSelection } =
    useViewContext();
  const { activeModes, activeView } = useLayoutContext();

  const selectedMode = activeModes[activeView];

  const [zoomInDisabled, setZoomInDisabled] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  // TODO: disabled for ux testable - needs more ticket detail
  // const [panNextDisabled, setPanNextDisabled] = useState(false);

  //TODO: Need to work on timeselection history
  const { timeSelection, timeSelectionHistory } = viewState;

  const zoomIn = useCallback(
    (params?: Partial<ZoomTo>) => {
      const prevTimeSelection = viewState.timeSelection;
      const [newStartTime, newEndTime] = ChartInteractionUtil.inCalculateBounds(
        prevTimeSelection,
        ANALYZE_MIN_ZOOM,
        params,
      );
      recordTimelineHistory();
      updateTimeSelection(
        {
          ...prevTimeSelection,
          startTime: newStartTime,
          endTime: newEndTime,
          streamingTimeInSeconds: undefined,
          timeRange: TimeRanges.CUSTOM,
        },
        true,
      );
    },
    [updateTimeSelection, recordTimelineHistory, viewState],
  );

  const zoomOut = useCallback(() => {
    const prevTimeSelection = viewState.timeSelection;
    const [newStartTime, newEndTime] = ChartInteractionUtil.outCalculateBounds(prevTimeSelection);
    recordTimelineHistory();
    updateTimeSelection(
      {
        ...prevTimeSelection,
        startTime: newStartTime,
        endTime: newEndTime,
        streamingTimeInSeconds: undefined,
        timeRange: TimeRanges.CUSTOM,
      },
      true,
    );
  }, [updateTimeSelection, recordTimelineHistory, viewState]);

  const panPrevious = useCallback(() => {
    const prevTimeSelection = viewState.timeSelection;
    const [newStartTime, newEndTime] =
      ChartInteractionUtil.panLeftCalculateBounds(prevTimeSelection);
    recordTimelineHistory();
    updateTimeSelection(
      {
        ...prevTimeSelection,
        startTime: newStartTime,
        endTime: newEndTime,
        streamingTimeInSeconds: undefined,
        timeRange: TimeRanges.CUSTOM,
      },
      true,
    );
  }, [updateTimeSelection, recordTimelineHistory, viewState]);

  const panNext = useCallback(() => {
    // disable pan next if in relative mode
    // if (streamingTimeInSeconds) return;

    // TODO: disabled for ux testable - needs more ticket detail
    // const newEndTime = dayjs(endTime).add(panStep, 's');

    const prevTimeSelection = viewState.timeSelection;
    const [newStartTime, newEndTime] =
      ChartInteractionUtil.panRightCalculateBounds(prevTimeSelection);
    recordTimelineHistory();
    updateTimeSelection(
      {
        ...prevTimeSelection,
        startTime: newStartTime,
        endTime: newEndTime,
        streamingTimeInSeconds: undefined,
        timeRange: TimeRanges.CUSTOM,
      },
      true,
    );
  }, [updateTimeSelection, recordTimelineHistory, viewState]);

  const jumpToNow = useCallback(() => {
    const prevTimeSelection = viewState.timeSelection;
    const now = dayjs();
    const diff = now.diff(prevTimeSelection.endTime);
    const newStartTime = prevTimeSelection.startTime.add(diff);
    const streamingSeconds = now.diff(newStartTime, 'seconds');
    updateTimeSelection(
      {
        ...prevTimeSelection,
        endTime: now,
        startTime: newStartTime,
        nowSelected: true,
        streamingTimeInSeconds: streamingSeconds,
        timeRange: TimeRangeUtil.calculateTimePreset('value', streamingSeconds)
          ? TimeRanges.PRESET
          : TimeRanges.CUSTOM,
      },
      true,
    );
  }, [updateTimeSelection, viewState.timeSelection]);

  const isAnalyze = selectedMode === ViewType.ANALYZE;
  const { track } = useAnalytics();

  const handleZoomIn = zoomIn;
  const handleZoomOut = zoomOut;
  const handlePanNext = panNext;
  const handlePanPrevious = panPrevious;
  const handleJumpToNow = jumpToNow;

  const { startTime, endTime } = timeSelection;

  const getRangeDiff = useCallback(
    () => dayjs(endTime).diff(dayjs(startTime), 'seconds'),
    [endTime, startTime],
  );

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    // set zoom to disabled on monitor and analyze when reaching max zoom durations
    const rangeDiff: number = getRangeDiff();
    const isAnalyzeZoom =
      !!matchPath(Paths.ANALYZE, pathname) || !!matchPath(Paths.DEMO_ANALYZE, pathname);
    setZoomInDisabled(
      isAnalyzeZoom ? rangeDiff <= ANALYZE_MIN_ZOOM : rangeDiff <= MONITOR_MIN_ZOOM,
    );

    // TODO: disabled for ux testable - needs more ticket detail
    // const newEndTime = dayjs(endTime).add(panStep, 's');
    // const isEndTimeInFuture: boolean = dayjs().diff(newEndTime, 'seconds') < 0;
    // setPanNextDisabled(isEndTimeInFuture || streamingTimeInSeconds !== undefined);
  }, [startTime, endTime, getRangeDiff, pathname]);

  useKeyPress(['=', '+'], handleZoomIn);
  useKeyPress(['-', '_'], handleZoomOut);
  useKeyPress(['u', 'U'], handleUndoTimeSelection);
  useKeyPress([','], handlePanPrevious);
  useKeyPress(['.'], handlePanNext);

  return (
    <StyledActions>
      <div className="group">
        <button
          data-testid="undo"
          type="button"
          onClick={() => {
            track('Chart Actions - Undo Timeshift', {
              undoTimeshift: 'clicked',
              currentPage: isAnalyze ? 'Analyze' : 'Monitor',
            });
            handleUndoTimeSelection();
          }}
          disabled={!timeSelectionHistory.length}
        >
          <Tooltip
            version="v2"
            direction="up"
            label={
              timeSelectionHistory.length && windowWidth > windowWidthBreakpoints.mobile ? (
                <ShortcutTooltip label="Reset" shortcut={'Press the "u" key.'} />
              ) : (
                <ShortcutTooltip label="Reset" />
              )
            }
          >
            <Icon name={ICONS.Undo} />
          </Tooltip>
        </button>
        <button
          data-testid="zoom-out"
          id="zoomOut"
          type="button"
          onClick={() => {
            track('Chart Actions - Zoom Out', {
              zoomOut: 'clicked',
              currentPage: isAnalyze ? 'Analyze' : 'Monitor',
            });
            handleZoomOut();
          }}
        >
          <Tooltip
            version="v2"
            direction="up"
            label={
              windowWidth > windowWidthBreakpoints.mobile ? (
                <ShortcutTooltip label="Zoom Out" shortcut={'Press the "-" key.'} />
              ) : (
                <ShortcutTooltip label="Zoom Out" />
              )
            }
          >
            <Icon name={ICONS.Minus} />
          </Tooltip>
        </button>
        <button
          data-testid="zoom-in"
          id="zoomIn"
          type="button"
          onClick={() => {
            track('Chart Actions - Zoom In', {
              zoomIn: 'clicked',
              currentPage: isAnalyze ? 'Analyze' : 'Monitor',
            });
            handleZoomIn();
          }}
          disabled={zoomInDisabled}
        >
          <Tooltip
            version="v2"
            direction="up"
            label={
              windowWidth > windowWidthBreakpoints.mobile ? (
                <ShortcutTooltip label="Zoom In" shortcut={'press the "+" key.'} />
              ) : (
                <ShortcutTooltip label="Zoom In" />
              )
            }
          >
            <Icon name={ICONS.Plus} />
          </Tooltip>
        </button>
        <button
          data-testid="pan-left"
          id="panLeft"
          type="button"
          onClick={() => {
            track('Chart Actions - Pan Previous', {
              panPrevious: 'clicked',
              currentPage: isAnalyze ? 'Analyze' : 'Monitor',
            });
            handlePanPrevious();
          }}
        >
          <Tooltip version="v2" direction="up" label="Back">
            <Icon name={ICONS.PanLeft} />
          </Tooltip>
        </button>
        <button
          // TODO: disabled for ux testable - needs more ticket detail
          // disabled={panNextDisabled}
          id="panRight"
          type="button"
          data-testid="pan-right"
          onClick={() => {
            track('Chart Actions - Pan Next', {
              panNext: 'clicked',
              currentPage: isAnalyze ? 'Analyze' : 'Monitor',
            });
            handlePanNext();
          }}
          disabled={!!timeSelection.streamingTimeInSeconds}
        >
          <Tooltip version="v2" direction="up" label="Forward">
            <Icon name={ICONS.PanRight} />
          </Tooltip>
        </button>
        {/* {isStreaming} */}
        <button
          id="now"
          data-testid="jump-to-now"
          type="button"
          onClick={() => {
            track('Chart Actions - Jump to Now', {
              jumpToNow: 'clicked',
              currentPage: isAnalyze ? 'Analyze' : 'Monitor',
            });
            handleJumpToNow();
          }}
        >
          {/* <Tooltip label={`Jump to ${currentEnvironment?.isBatch ? `latest` : `now`}`}>
              <Icon name={ICONS.Now} width="16" height="16" />
            </Tooltip> */}
          <Tooltip version="v2" direction="left" label="Jump to now">
            <Icon name={ICONS.Now} />
          </Tooltip>
        </button>
      </div>
    </StyledActions>
  );
};

const ShortcutTooltip: React.FC<{ label: string; shortcut?: string }> = ({ label, shortcut }) => {
  return (
    <>
      {`${label}`}
      <br />
      {shortcut && <i>{`Shortcut: ${shortcut}`}</i>}
    </>
  );
};
